import React from 'react'
import { useTranslation } from 'react-i18next'
import { Hand, UserCog } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { cn } from '@/lib/utils'
import type { AttendanceModuleConfig, GuestFieldMode, ModuleFieldComponentProps } from '@/types/space-wizard'
import type { Space } from '@/types/spaces'
import { RetentionField } from '@/components/features/spaces/retention'
import { RETENTION_DEFAULT_MONTHS, pendingShortening } from '@/components/features/spaces/retentionUtils'
import { DEFAULT_AUTO_CHECKOUT, DEFAULT_KIOSK_RESET_SECONDS, type AttendanceConfigErrors } from './attendanceUtils'

interface Props extends ModuleFieldComponentProps<AttendanceModuleConfig> {
  errors?: AttendanceConfigErrors | null
  /** The space edit adds the retention period (F-203a); the wizard keeps the default. */
  context?: 'create' | 'edit'
  space?: Space
}

/**
 * The attendance setup (F-78 §6.2): who marks presence, the capacity, the end-of-day closing.
 * Short, so the wizard asks it on the same screen as the space details (PADROES-UI §14), and the
 * space edit shows the very same component.
 */
export const AttendanceSpaceFields: React.FC<Props> = ({ config, onChange, disabled = false, errors, context, space }) => {
  const { t } = useTranslation(['attendance', 'dayPasses'])
  const selfCheckIn = config.selfCheckIn ?? 'off'
  const options = [
    { value: 'off' as const, icon: UserCog, label: t('config.selfCheckIn.off') },
    { value: 'button' as const, icon: Hand, label: t('config.selfCheckIn.button') }
  ]

  const kiosk = config.kiosk ?? {}
  const setKiosk = (next: NonNullable<AttendanceModuleConfig['kiosk']>) => onChange({ ...config, kiosk: { ...kiosk, ...next } })
  const modes: GuestFieldMode[] = ['off', 'optional', 'required']
  const guestField = (field: 'company' | 'phone') => {
    const current = kiosk.guestFields?.[field] ?? 'off'
    return (
      <div className="space-y-2">
        <label className="text-sm font-medium text-gray-700">{t(`config.kiosk.guestFields.${field}`)}</label>
        <div className="flex flex-wrap gap-2" role="radiogroup" aria-label={t(`config.kiosk.guestFields.${field}`)}>
          {modes.map((mode) => (
            <button
              key={mode}
              type="button"
              role="radio"
              aria-checked={current === mode}
              disabled={disabled}
              onClick={() => setKiosk({ guestFields: { ...kiosk.guestFields, [field]: mode } })}
              className={cn(
                'whitespace-nowrap rounded-lg px-3 py-2 text-sm transition-colors',
                current === mode ? 'bg-primary-100 text-primary-700' : 'hover:bg-gray-100',
                disabled && 'cursor-not-allowed opacity-50'
              )}
            >
              {t(`config.kiosk.guestFields.modes.${mode}`)}
            </button>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-5">
      <div className="space-y-2">
        <label className="text-sm font-medium text-gray-700">{t('config.selfCheckIn.label')}</label>
        <div className="flex flex-wrap gap-2" role="radiogroup" aria-label={t('config.selfCheckIn.label')}>
          {options.map(({ value, icon: Icon, label }) => (
            <button
              key={value}
              type="button"
              role="radio"
              aria-checked={selfCheckIn === value}
              disabled={disabled}
              onClick={() => onChange({ ...config, selfCheckIn: value })}
              className={cn(
                'flex items-center gap-2 whitespace-nowrap rounded-lg px-3 py-2 text-sm transition-colors',
                selfCheckIn === value ? 'bg-primary-100 text-primary-700' : 'hover:bg-gray-100',
                disabled && 'cursor-not-allowed opacity-50'
              )}
            >
              <Icon className="h-4 w-4 flex-shrink-0" />
              <span>{label}</span>
            </button>
          ))}
        </div>
        <p className="text-xs text-gray-500">
          {selfCheckIn === 'button' ? t('config.selfCheckIn.buttonHint') : t('config.selfCheckIn.offHint')}
        </p>
      </div>

      <div className="space-y-2">
        <label htmlFor="attendance-capacity" className="text-sm font-medium text-gray-700">{t('config.capacity.label')}</label>
        <Input
          id="attendance-capacity"
          type="number"
          inputMode="numeric"
          value={config.capacity ?? ''}
          disabled={disabled}
          placeholder={t('config.capacity.placeholder')}
          onChange={(e) => {
            const raw = e.target.value
            onChange({ ...config, capacity: raw === '' ? null : Number(raw) })
          }}
          className={cn('max-w-[12rem]', errors?.capacity && 'border-red-500')}
          aria-invalid={Boolean(errors?.capacity)}
        />
        {errors?.capacity
          ? <p role="alert" className="text-xs text-red-600">{errors.capacity}</p>
          : <p className="text-xs text-gray-500">{t('config.capacity.hint')}</p>}
      </div>

      <div className="space-y-2">
        <label htmlFor="attendance-auto-checkout" className="text-sm font-medium text-gray-700">{t('config.autoCheckout.label')}</label>
        <Input
          id="attendance-auto-checkout"
          type="time"
          value={config.autoCheckoutTime ?? DEFAULT_AUTO_CHECKOUT}
          disabled={disabled}
          onChange={(e) => onChange({ ...config, autoCheckoutTime: e.target.value })}
          className={cn('max-w-[12rem]', errors?.autoCheckoutTime && 'border-red-500')}
          aria-invalid={Boolean(errors?.autoCheckoutTime)}
        />
        {errors?.autoCheckoutTime
          ? <p role="alert" className="text-xs text-red-600">{errors.autoCheckoutTime}</p>
          : <p className="text-xs text-gray-500">{t('config.autoCheckout.hint')}</p>}
      </div>

      <p className="rounded-lg bg-gray-50 p-3 text-xs text-gray-600">{t('config.showWhoIsHere')}</p>

      {/* F-249: who has no plan spends a day pass at the first check-in of the day. */}
      <div className="flex items-center justify-between gap-4">
        <div className="min-w-0">
          <p className="text-sm font-medium text-gray-700">{t('dayPasses:config.label')}</p>
          <p className="text-xs text-gray-500">{t('dayPasses:config.hint')}</p>
        </div>
        <Switch
          checked={config.dayPasses === true}
          disabled={disabled}
          className="data-[state=checked]:bg-primary"
          aria-label={t('dayPasses:config.label')}
          onCheckedChange={(checked) => onChange({ ...config, dayPasses: checked })}
          data-testid="attendance-day-passes"
        />
      </div>

      <div className="space-y-5 border-t pt-5">
        <div className="space-y-1">
          <h4 className="text-sm font-semibold text-gray-900">{t('config.kiosk.title')}</h4>
          <p className="text-xs text-gray-500">{t('config.kiosk.hint')}</p>
        </div>

        {guestField('company')}
        {guestField('phone')}

        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0">
            <p className="text-sm font-medium text-gray-700">{t('config.kiosk.notifyHost.label')}</p>
            <p className="text-xs text-gray-500">{t('config.kiosk.notifyHost.hint')}</p>
          </div>
          <Switch
            checked={kiosk.notifyHost ?? true}
            disabled={disabled}
            className="data-[state=checked]:bg-primary"
            aria-label={t('config.kiosk.notifyHost.label')}
            onCheckedChange={(checked) => setKiosk({ notifyHost: checked })}
          />
        </div>

        <div className="space-y-2">
          <label htmlFor="attendance-reset-seconds" className="text-sm font-medium text-gray-700">{t('config.kiosk.resetSeconds.label')}</label>
          <Input
            id="attendance-reset-seconds"
            type="number"
            inputMode="numeric"
            value={kiosk.resetSeconds ?? DEFAULT_KIOSK_RESET_SECONDS}
            disabled={disabled}
            onChange={(e) => setKiosk({ resetSeconds: e.target.value === '' ? undefined : Number(e.target.value) })}
            className={cn('max-w-[12rem]', errors?.resetSeconds && 'border-red-500')}
            aria-invalid={Boolean(errors?.resetSeconds)}
          />
          {errors?.resetSeconds
            ? <p role="alert" className="text-xs text-red-600">{errors.resetSeconds}</p>
            : <p className="text-xs text-gray-500">{t('config.kiosk.resetSeconds.hint')}</p>}
        </div>
      </div>

      {context === 'edit' && (
        <RetentionField
          module="attendance"
          value={config.retentionMonths ?? RETENTION_DEFAULT_MONTHS}
          pending={pendingShortening(space, 'attendance')}
          onChange={(months) => onChange({ ...config, retentionMonths: months })}
          error={errors?.retentionMonths}
          disabled={disabled}
        />
      )}
    </div>
  )
}
